import { AuthUtils } from 'src/services/Auth/Auth.utils';
import { roles } from 'src/api/services/RoleClient';

const ADMIN_VIEWED_REQUESTS_KEY = 'admin-viewed-requests';
const MAX_VIEWED_REQUESTS = 1000; // Kill switch: maximum number of requests to store
const CLEANUP_BATCH_SIZE = 200; // Remove this many oldest entries when limit is exceeded

/**
 * Centralized utility for managing admin viewed requests
 * Ensures consistency across all components that need to track/check viewed status
 */
export class AdminViewedRequestsManager {
  /**
   * Check if current user is admin
   */
  static isCurrentUserAdmin(): boolean {
    const userType = AuthUtils.getPayload()?.user_type;
    return userType === roles.admin;
  }

  /**
   * Get list of admin viewed request IDs from localStorage
   */
  static getViewedRequestIds(): string[] {
    if (!this.isCurrentUserAdmin()) return [];
    
    try {
      const stored = localStorage.getItem(ADMIN_VIEWED_REQUESTS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /**
   * Clean up old entries to prevent localStorage overflow
   * Removes oldest entries when storage exceeds limits
   */
  private static cleanupOldEntries(viewedList: string[]): string[] {
    if (viewedList.length <= MAX_VIEWED_REQUESTS) {
      return viewedList;
    }

    // Keep only the most recent entries (FIFO - remove oldest first)
    const cleanedList = viewedList.slice(-MAX_VIEWED_REQUESTS + CLEANUP_BATCH_SIZE);
        
    return cleanedList;
  }

  /**
   * Check if a specific request has been viewed by admin
   */
  static isRequestViewed(requestId: string): boolean {
    if (!this.isCurrentUserAdmin()) return false;
    
    const viewedList = this.getViewedRequestIds();
    return viewedList.includes(requestId);
  }

  /**
   * Mark a request as viewed by admin
   */
  static markRequestAsViewed(requestId: string): void {
    if (!this.isCurrentUserAdmin()) return;
    
    try {
      const viewed = this.getViewedRequestIds();
      if (!viewed.includes(requestId)) {
        viewed.push(requestId);
        
        // Apply kill switch cleanup before saving
        const cleanedViewed = this.cleanupOldEntries(viewed);
        
        localStorage.setItem(ADMIN_VIEWED_REQUESTS_KEY, JSON.stringify(cleanedViewed));
        
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('admin-viewed-requests-updated', {
          detail: { requestId, viewedList: cleanedViewed }
        }));
      }
    } catch (error) {
      console.warn('Failed to update admin viewed requests:', error);
      // If localStorage is full, attempt emergency cleanup
      try {
        const currentViewed = this.getViewedRequestIds();
        const emergencyCleanup = currentViewed.slice(-Math.floor(MAX_VIEWED_REQUESTS / 2));
        localStorage.setItem(ADMIN_VIEWED_REQUESTS_KEY, JSON.stringify(emergencyCleanup));
        console.warn('AdminViewedRequestsManager: Emergency cleanup performed due to storage error');
      } catch (emergencyError) {
        console.error('AdminViewedRequestsManager: Emergency cleanup failed', emergencyError);
        this.clearAllViewedRequests();
      }
    }
  }

  /**
   * Remove a request from viewed list (useful for testing or cleanup)
   */
  static removeViewedRequest(requestId: string): void {
    if (!this.isCurrentUserAdmin()) return;
    
    try {
      const viewed = this.getViewedRequestIds();
      const filtered = viewed.filter(id => id !== requestId);
      localStorage.setItem(ADMIN_VIEWED_REQUESTS_KEY, JSON.stringify(filtered));
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('admin-viewed-requests-updated', {
        detail: { requestId, viewedList: filtered }
      }));
    } catch (error) {
      console.warn('Failed to remove admin viewed request:', error);
    }
  }

  /**
   * Clear all viewed requests (useful for testing)
   */
  static clearAllViewedRequests(): void {
    if (!this.isCurrentUserAdmin()) return;
    
    try {
      localStorage.removeItem(ADMIN_VIEWED_REQUESTS_KEY);
      window.dispatchEvent(new CustomEvent('admin-viewed-requests-updated', {
        detail: { requestId: null, viewedList: [] }
      }));
    } catch (error) {
      console.warn('Failed to clear admin viewed requests:', error);
    }
  }

  /**
   * Filter an array of request IDs to exclude viewed ones
   * Used for count filtering
   */
  static filterUnviewedRequestIds(requestIds: string[]): string[] {
    if (!this.isCurrentUserAdmin()) return requestIds;
    
    const viewedList = this.getViewedRequestIds();
    return requestIds.filter(id => !viewedList.includes(id));
  }

  /**
   * Check if a request should be highlighted (not viewed by admin)
   * Used for row highlighting logic
   */
  static shouldHighlightRequest(requestId: string): boolean {
    if (!this.isCurrentUserAdmin()) return false;
    
    return !this.isRequestViewed(requestId);
  }
}
